import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { CiqIcon, Ico } from "@/lib/ciq-icons";
import { useAppSelector } from "@/store/index";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

interface NavbarProps {
  onMenuOpen?: () => void;
}

export function Navbar({ onMenuOpen }: NavbarProps) {
  const user = useAppSelector((s) => s.auth.user);
  const { logout } = useAuth();
  const { lang, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const pageTitles: Record<string, string> = {
    "/dashboard": t("sidebar.dashboard"),
    "/generate": t("sidebar.generate"),
    "/history": t("sidebar.history"),
    "/templates": t("sidebar.templates"),
    "/favorites": t("sidebar.favorites"),
    "/profile": t("sidebar.profile"),
    "/pricing": t("sidebar.billing"),
    "/voice": t("sidebar.voice"),
    "/admin": "Admin",
  };

  const pageTitle = pageTitles[location.pathname] ?? "CONTENT.IQ";

  const planLabels: Record<string, string> = { admin: "Admin", business: "Business", pro: "Pro" };
  const plan = planLabels[user?.role ?? ""] ?? "Free";

  const credits = user?.credits?.remaining ?? 0;

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "U";

  return (
    <div
      className="row between navbar-container"
      style={{
        padding: "12px 18px",
        borderBottom: "1px solid rgba(255,255,255,0.25)",
        background: "rgba(253,252,249,0.68)",
        backdropFilter: "blur(22px) saturate(180%) brightness(1.02)",
        WebkitBackdropFilter: "blur(22px) saturate(180%) brightness(1.02)",
        flex: "0 0 auto",
      }}
    >
      {/* ── Left side ── */}
      <div className="row" style={{ gap: 18 }}>
        {/* Logo — masqué sur mobile */}
        <div className="ciq-mark navbar-logo">
          <span className="dot">C</span>
          <span className="name" style={{ display: "var(--navbar-name-display, inline)" }}>
            <b>CONTENT</b>
            <span>.IQ</span>
          </span>
        </div>

        {/* Titre de la page — mobile seulement */}
        <span className="navbar-page-title">{pageTitle}</span>

        <div className="row navbar-plan-badge" style={{ gap: 6 }}>
          <span className="pill" style={{ borderRadius: 8 }}>
            <span className="swatch" style={{ background: "var(--accent)" }} />
            {plan}
          </span>
          <span className="pill t-mono" style={{ borderRadius: 8 }}>
            <Ico icon={CiqIcon.zap} size={12} />
            {credits} cr.
          </span>
        </div>
      </div>

      {/* ── Right side ── */}
      <div className="row" style={{ gap: 8 }}>
        {/* Crédits — desktop seulement */}
        <span
          className="pill t-mono navbar-credits-desktop"
          style={{ fontSize: 11, borderRadius: 8 }}
        >
          <span style={{ color: "var(--accent)" }}>
            <Ico icon={CiqIcon.zap} size={11} />
          </span>
          {credits}
        </span>

        <div className="seg navbar-lang-toggle">
          <button
            type="button"
            className={lang === "fr" ? "on" : ""}
            onClick={() => changeLanguage("fr")}
          >
            FR
          </button>
          <button
            type="button"
            className={lang === "en" ? "on" : ""}
            onClick={() => changeLanguage("en")}
          >
            EN
          </button>
        </div>

        <button
          type="button"
          className="btn btn-ghost btn-sm hide-mobile"
          onClick={() => navigate("/generate")}
        >
          <Ico icon={CiqIcon.sparkle} size={20} />
          {t("nav.generate")}
        </button>

        {/* Avatar + dropdown — masqué sur mobile */}
        <div ref={menuRef} className="hide-mobile" style={{ position: "relative" }}>
          <button
            type="button"
            className="nav-avatar-btn"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu compte"
            aria-expanded={menuOpen}
          >
            {initials}
          </button>
          {menuOpen && (
            <div
              style={{
                position: "absolute",
                top: 36,
                right: 0,
                background: "var(--bg-elev)",
                border: "1px solid var(--line)",
                borderRadius: 10,
                boxShadow: "var(--shadow-pop)",
                padding: 4,
                minWidth: 172,
                zIndex: 50,
              }}
            >
              <button
                type="button"
                className="nav-dropdown-item"
                onClick={() => {
                  navigate("/profile");
                  setMenuOpen(false);
                }}
              >
                <Ico icon={CiqIcon.user} size={15} />
                Mon profil
              </button>
              <div className="hr" style={{ margin: "4px 0" }} />
              <button
                type="button"
                className="nav-dropdown-item danger"
                onClick={() => {
                  logout();
                  setMenuOpen(false);
                }}
              >
                <Ico icon={CiqIcon.logout} size={15} />
                {t("profile.logoutBtn")}
              </button>
            </div>
          )}
        </div>

        {/* Burger — mobile seulement */}
        <button
          type="button"
          className="navbar-mobile-hamburger"
          onClick={onMenuOpen}
          aria-label="Ouvrir le menu"
        >
          <span className="navbar-burger-bars">
            <i />
            <i style={{ width: "70%" }} />
            <i />
          </span>
        </button>
      </div>
    </div>
  );
}
